import { getScanPhotoImageUrl, KaiProApiClient } from '@kaiizen/kai-pro-api';

import { getBinary } from '@kaiizen/zod-less-api';
import { PhotoSize } from '../../contexts/syncing/Photos/PhotoGalleryContext';
import { ConnectionInfo } from '../../FileSystem/FileSystemInterface';
import { InMemoryFileSystem } from '../../FileSystem/InMemoryFileSystem';
import { PHOTO_GALLERY_CONFIG } from '../common';

const DEFAULT_QUALITY = PHOTO_GALLERY_CONFIG.defaultQuality;

const getScaledImageFromBlob = async (data: Blob, size: number): Promise<Blob> => {
  const img = document.createElement('img');
  const tempUrl = URL.createObjectURL(data);
  img.src = tempUrl;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  return new Promise<Blob>(resolve => {
    img.onload = () => {
      const scaleFactor = size / Math.max(img.width, img.height);
      canvas.width = img.width * scaleFactor;
      canvas.height = img.height * scaleFactor;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        blob => {
          URL.revokeObjectURL(tempUrl);
          resolve(blob!);
        },
        'image/jpeg',
        DEFAULT_QUALITY,
      );
    };
  });
};

/**
 * In-memory file system with browser-specific extensions for web use.
 * Provides Canvas-based image scaling and a sync `pathToUri` that returns
 * `blob:` object URLs (browsers can't load `file://` URIs from in-memory data).
 *
 * Lifecycle: a blob URL is minted at write time and revoked on delete/overwrite/move,
 * so `pathToUri(path)` is a sync map lookup and the URL stays valid as long as the
 * file does. Same path → same URL (no duplicates, no leaks).
 */
export class WebInMemoryFileSystem extends InMemoryFileSystem {
  private blobUrlsByPath = new Map<string, string>();

  private revokeUriAt(path: string): void {
    const url = this.blobUrlsByPath.get(path);
    if (url) {
      URL.revokeObjectURL(url);
      this.blobUrlsByPath.delete(path);
    }
  }

  override async writeAsBlobAsync(path: string, content: Blob): Promise<void> {
    this.revokeUriAt(path);
    await super.writeAsBlobAsync(path, content);
    this.blobUrlsByPath.set(path, URL.createObjectURL(content));
  }

  override async deleteAsync(path: string): Promise<void> {
    this.revokeUriAt(path);
    await super.deleteAsync(path);
  }

  override async moveAsync(options: { from: string; to: string }): Promise<void> {
    this.revokeUriAt(options.to);
    const fromUrl = this.blobUrlsByPath.get(options.from);
    await super.moveAsync(options);
    if (fromUrl) {
      this.blobUrlsByPath.delete(options.from);
      this.blobUrlsByPath.set(options.to, fromUrl);
    }
  }

  override async deleteDirectoryAsync(path: string): Promise<void> {
    const prefix = path.replace(/\/+$/, '') + '/';
    for (const filePath of [...this.blobUrlsByPath.keys()]) {
      if (filePath === path || filePath.startsWith(prefix)) this.revokeUriAt(filePath);
    }
    await super.deleteDirectoryAsync(path);
  }

  override clear(): void {
    for (const url of this.blobUrlsByPath.values()) URL.revokeObjectURL(url);
    this.blobUrlsByPath.clear();
    super.clear();
  }

  override pathToUri(path: string): string {
    const url = this.blobUrlsByPath.get(path);
    if (!url) throw new Error(`pathToUri: no blob URL for path ${path} (file not written or already deleted)`);
    return url;
  }

  override async scaleImageFile(loadFromPath: string, saveToPath: string, size: number): Promise<string> {
    const data = await this.readAsBlobAsync(loadFromPath);
    const scaled = await getScaledImageFromBlob(data, size);
    await this.writeAsBlobAsync(saveToPath, scaled);
    return saveToPath;
  }

  override async uploadPhoto(
    filePath: string,
    scanId: string,
    photoId: string,
    _connectionInfo: ConnectionInfo,
    timeoutSeconds?: number,
    onProgress?: (progress: number) => void,
  ): Promise<void> {
    const data = await this.readAsBlobAsync(filePath);
    const kaiProApiClient = new KaiProApiClient({
      apiUrl: _connectionInfo.apiUrl,
      token: _connectionInfo.token,
    });
    await kaiProApiClient.createScanPhoto({ scanId, id: photoId }, data, timeoutSeconds, onProgress);
  }

  override async downloadPhoto(photoId: string, filePath: string, size: PhotoSize, _connectionInfo: ConnectionInfo): Promise<string> {
    // size === 'original' means request the full-resolution photo (no maxDimension)
    const maxDimension = typeof size === 'number' ? size : undefined;
    const url = getScanPhotoImageUrl(photoId, maxDimension ? { maxDimension } : undefined);
    const blob = await getBinary(url);
    await this.writeAsBlobAsync(filePath, blob);
    return filePath;
  }
}
